import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStatus } from "context/OAuth/AuthContext";
import { toast } from "react-toastify";
import { Eye, EyeOff, XCircle, Loader2 } from "lucide-react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import { getLoginMarketingLinks } from "@constants/loginMarketingLinks";
import useAuthLang from "@hooks/useAuthLang";
import { authPath } from "utils/authLanguage";

const links = getLoginMarketingLinks();

const resetSchema = Yup.object({
  password: Yup.string()
    // .min(8, "Password must be at least 8 characters")
    .required("Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

const PasswordResetConfirm = () => {
  const [searchParams] = useSearchParams();

  // URL parameters matching: .../verify-link?token=XYZ&email=abc@gmail.com&expires=123
  const token = searchParams.get("token");
  const expires = searchParams.get("expires");
  const email = searchParams.get("email");

  const { t } = useTranslation();
  const authLang = useAuthLang();

  // Destructure functions from AuthContext
  const {
    confirmPasswordReset,
    verifyPasswordResetLink,
    requestPasswordConfirm,
  } = useAuthStatus();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // States for API Token Validation
  const [isValidatingToken, setIsValidatingToken] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const navigate = useNavigate();
  // =========================================================
  // WORDPRESS API TOKEN VALIDATION INTEGRATION
  // =========================================================
  useEffect(() => {
    const validateResetToken = async () => {
      // Agar minimum requirements (token ya email) URL me na hon
      console.log(token, email, expires);
      if (!token || !email) {
        setIsTokenValid(false);
        setIsValidatingToken(false);
        return;
      }

      try {
        setIsValidatingToken(true);

        // Payload jo context function ko pass hoga
        const payload = {
          token: token,
          email: email,
          expires: expires, // Agar WordPress expiration check handle karta hai URL key 'expires' se
        };

        // Auth context ki API hit ki
        const res = await verifyPasswordResetLink(payload);

        // Native fetch handles response status via 'ok' property
        if (res && res.ok) {
          const data = await res.json();
          // Agar backend custom success flag deta hai to: data.success ya res.ok verify karega
          setIsTokenValid(true);
        } else {
          // Backend custom error status parse karne k liye
          try {
            const errorData = await res.json();
            console.error("WordPress Verification Failed:", errorData?.message);
          } catch (e) {
            console.error("Could not parse error JSON");
          }
          setIsTokenValid(false);
        }
      } catch (error) {
        console.error("Token validation error:", error);
        setIsTokenValid(false);
      } finally {
        setIsValidatingToken(false);
      }
    };

    validateResetToken();
  }, [token, email, expires, verifyPasswordResetLink]);

  // Formik Logic (Submit new password)
  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: resetSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // WordPress New Password submission API call
        const { ok, data } = await requestPasswordConfirm({
          token,
          email, // safe keeping for backend reference
          new_password: values.password,
          confirm_password: values.confirmPassword,
        });

        if (ok) {
          toast.success(t("Password updated successfully!"));
          navigate(authPath("login", authLang));
        } else {
          toast.error(data?.message || t("Failed to reset password."));
        }
      } catch (err) {
        console.error(err);
        toast.error(t("Something went wrong. Please try again."));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-[450px] flex flex-col justify-center">
      {isValidatingToken ? (
        // 1. LOADING STATE
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-10 w-10 animate-spin text-[#15AAFC] mb-4" />
          <p className="text-[#535862] text-sm font-medium">
            {t("Verifying your reset link...")}
          </p>
        </div>
      ) : !isTokenValid ? (
        // 2. EXPIRED / INVALID LINK ERROR STATE
        <div className="text-center py-8 px-4 animate-fade-in">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-50 mb-4">
            <XCircle className="h-8 w-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-[600] text-[#181D27] mb-2">
            {t("Link Expired or Invalid")}
          </h1>
          <p className="text-[#535862] text-sm sm:text-base mb-8 max-w-sm mx-auto leading-relaxed">
            {t(
              "This password reset link is no longer valid or has expired. Please request a new link.",
            )}
          </p>
          <Link
            to={authPath("password-reset", authLang)}
            className="inline-block py-3 px-6 bg-[#0066EE] text-white font-semibold rounded-xl text-sm shadow hover:bg-[#0771EF] transition"
          >
            {t("Request New Link")}
          </Link>
        </div>
      ) : (
        // 3. ACTUAL PASSWORD CHANGE FORM
        <div className="animate-fade-in">
          <h1 className="text-center text-2xl sm:text-[30px] font-[600] text-[#181D27] mb-2">
            {t("Reset Your Password")}
          </h1>
          <p className="text-center text-[#535862] text-xs sm:text-sm mb-8">
            {t("Reset Your Password to Continue")}
          </p>

          <form onSubmit={formik.handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Password Field */}
              <div>
                <label className="block text-xs text-[#414651] mb-1 font-medium">
                  {t("Password")} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    autoComplete="new-password"
                    placeholder={t("Password")}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-3 py-2.5 pr-10 border rounded-lg text-sm text-[#1B1F26] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#15AAFC] focus:border-[#15AAFC] ${
                      formik.touched.password && formik.errors.password
                        ? "border-red-500 ring-1 ring-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className="text-red-500 text-xs mt-1 font-medium">
                    {formik.errors.password}
                  </div>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block text-xs text-[#414651] mb-1 font-medium">
                  {t("Confirm Password")}{" "}
                  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    autoComplete="new-password"
                    placeholder={t("Confirm Password")}
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-3 py-2.5 pr-10 border rounded-lg text-sm text-[#1B1F26] placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#15AAFC] focus:border-[#15AAFC] ${
                      formik.touched.confirmPassword &&
                      formik.errors.confirmPassword
                        ? "border-red-500 ring-1 ring-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {formik.touched.confirmPassword &&
                  formik.errors.confirmPassword && (
                    <div className="text-red-500 text-xs mt-1 font-medium">
                      {formik.errors.confirmPassword}
                    </div>
                  )}
              </div>
            </div>

            {/* Centered Submit Button */}
            <div className="flex justify-center pt-2">
              <button
                type="submit"
                disabled={formik.isSubmitting}
                className="w-full sm:max-w-[320px] py-2.5 rounded-lg text-white font-medium text-sm transition shadow-sm bg-[#15AAFC] hover:bg-[#0771EF] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {formik.isSubmitting ? <ButtonLoader /> : t("Submit")}
              </button>
            </div>
          </form>

          {/* Bottom Login Option */}
          <div className="text-center mt-6 text-xs text-gray-500">
            <span className="block mb-2 text-[#858585]">— OR —</span>
            <p>
              {t("Don't have an account?")}{" "}
              <Link
                to={authPath("login", authLang)}
                className="text-[#15AAFC] font-semibold hover:underline"
              >
                {t("Login")}
              </Link>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PasswordResetConfirm;
